import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { RouterParamLoaderService } from '@app/core/services';
import { notNull, sanitize } from '@app/shared/utils';
import { BehaviorSubject, combineLatest, debounceTime, startWith } from 'rxjs';
import { Collection } from '../../models';
import { TrailsService } from '../../services';

@Component({
  templateUrl: './sidenav-learning-trails.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidenavLearningTrailsComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly trailsService = inject(TrailsService);
  public search = new FormControl(undefined);
  public collections$ = new BehaviorSubject<Collection[]>([]);
  public countAllCourses: number;
  public allFilteredCollections = true;
  public collectionsSelected$ = new BehaviorSubject<number[]>([]);
  public coursesSelected$ = new BehaviorSubject<number[]>([]);
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _routerParamLoaderService = inject(RouterParamLoaderService);

  public ngOnInit(): void {
    this.countCourses();
  }

  public ngAfterViewInit(): void {
    this.onSearch();
  }

  public ngOnDestroy(): void {
    this.collections$.complete();
    this.collectionsSelected$.complete();
    this.coursesSelected$.complete();
  }

  public onShowAll(): void {
    this.allFilteredCollections = true;
    const collections = [];
    const courses = [];
    this.collections$.value.forEach((collection) => {
      collection.cursos.forEach((course) => {
        collections.push(collection.id);
        courses.push(course.id);
      });
    });
    this.collectionsSelected$.next(collections);
    this.coursesSelected$.next(courses);
    this.search.setValue(undefined, { emitEvent: false });
  }

  public onFilterCollection(id: number): void {
    if (this.allFilteredCollections) {
      this.allFilteredCollections = false;
      this.collectionsSelected$.next([id]);
      this.validAllFilteredCollections();
    } else {
      let data = [];
      if (this.isSelected(id)) {
        data = this.collectionsSelected$.value.filter((collection) => collection !== id);
        if (!data?.length) {
          this.onShowAll();
        }
      } else {
        data = this.collectionsSelected$.value;
        data.push(id);
      }
      if (!!data?.length) {
        this.collectionsSelected$.next(data);
        this.validAllFilteredCollections();
      }
    }
  }

  public isSelected(id: number): boolean {
    return this.collectionsSelected$.value?.includes(id);
  }

  public get labelUcIvca(): string {
    if (this.trailsService.uc$.value) return 'menu.universidade-corporativa';
    if (this.trailsService.ivca$.value) return 'menu.ivca';
    return 'menu.trilhas-aprendizagem';
  }

  private searchCourse(title: string): void {
    if (title) {
      const courses = [];
      const collections = [];
      this.collections$.value.forEach((collection) => {
        collection.cursos.forEach((course) => {
          if (sanitize(course.titulo).includes(sanitize(title))) {
            collections.push(collection.id);
            courses.push(course.id);
          }
        });
      });
      this.collectionsSelected$.next(collections);
      this.coursesSelected$.next(courses);
      this.validAllFilteredCollections();
      this._routerParamLoaderService.updateReq({ search: title });
    } else {
      this._routerParamLoaderService.updateReq({ search: null });
      this.onShowAll();
    }
    this._changeDetectorRef.detectChanges();
  }

  private onSearch(): void {
    const search$ = this.search.valueChanges.pipe(debounceTime(700), startWith(this.search.value));
    combineLatest([search$, this.collections$]).subscribe(([value]) => this.searchCourse(value));
  }

  private countCourses(): void {
    this.collections$.pipe(notNull()).subscribe((collections) => {
      this.countAllCourses = collections.reduce((accumulator, collection) => accumulator + collection.cursos.length, 0);
    });
  }

  private validAllFilteredCollections(): void {
    const collectionsSelected = this.collectionsSelected$.value.length;
    if (!collectionsSelected || collectionsSelected === this.collections$.value?.length) {
      this.allFilteredCollections = true;
    } else {
      this.allFilteredCollections = false;
    }
  }
}
